<script lang="ts">
  import '../app.css';
  import DesktopSideNav from '$lib/components/DesktopSideNav.svelte';
  import MobileTabBar from '$lib/components/MobileTabBar.svelte';
  import PwaInstallBanner from '$lib/components/PwaInstallBanner.svelte';
  import Toast from '$lib/components/Toast.svelte';
  import MfaReauthModal from '$lib/components/MfaReauthModal.svelte';
  import DemoBanner from '$lib/components/DemoBanner.svelte';
  import { isAuthenticated, auth } from '$lib/stores/auth.js';
  import { startNotificationPolling } from '$lib/stores/notifications.js';
  import { initThemeClient } from '$lib/stores/theme.js';
  import { pwa } from '$lib/stores/pwa.js';
  import { tierStore } from '$lib/tier.js';
  import { restoreSession } from '$lib/api.js';
  import { loadFeatureFlags, resetFeatureFlags } from '$lib/featureFlags.js';
  import { resetProfile } from '$lib/stores/profile.js';
  import { goto } from '$app/navigation';
  import { page } from '$app/state';
  import { get } from 'svelte/store';
  import { onMount } from 'svelte';
  import { env } from '$env/dynamic/public';
  import { initPostHog, identifyUser, resetUser } from '$lib/posthog.js';

  let { children } = $props();


  const PUBLIC_ROUTE_PREFIXES = ['/login', '/assets', '/landing', '/privacy', '/terms', '/pricing', '/safety', '/help'];

  function isPublicRoute(pathname: string): boolean {
    return pathname === '/' || PUBLIC_ROUTE_PREFIXES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
  }

  const MARKETING_ROUTE_PREFIXES = ['/landing', '/privacy', '/terms', '/pricing', '/safety'];

  function isMarketingRoute(pathname: string): boolean {
    return pathname === '/' || MARKETING_ROUTE_PREFIXES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
  }

  let isAuth = $state(get(isAuthenticated));
  let initialized = $state(false);

  $effect(() => isAuthenticated.subscribe(v => { isAuth = v; }));

  let currentPathname = $derived(page.url.pathname);

  $effect(() => {
    if (typeof document === 'undefined') return;
    if (isMarketingRoute(currentPathname)) {
      document.documentElement.classList.remove('dark');
    } else {
      initThemeClient();
    }
  });

  let showMainTabs = $derived(
    isAuth &&
    !isPublicRoute(currentPathname) &&
    !currentPathname.startsWith('/connect') &&
    // The demo Akahu replica stands in for an external redirect (real Akahu is a
    // separate site) — hide the app nav so it reads as its own flow.
    !currentPathname.startsWith('/demo/akahu') &&
    !currentPathname.startsWith('/setup-passkey'),
  );

  const siteOrigin = 'https://autopaylot.co.nz';
  /** Public URL of the current page (path only; no UTM) — matches OG / Slack / LinkedIn unfurl expectations. */
  let sharePageUrl = $derived(`${siteOrigin}${page.url.pathname || '/'}`);

  onMount(() => {
    initThemeClient();
    pwa.init();
    console.debug('[Layout] PUBLIC_POSTHOG_KEY =', env.PUBLIC_POSTHOG_KEY ? `${env.PUBLIC_POSTHOG_KEY.slice(0, 8)}...` : env.PUBLIC_POSTHOG_KEY);
    void initPostHog(env.PUBLIC_POSTHOG_KEY);

    let unsub: (() => void) | undefined;
    let stopPolling: (() => void) | undefined;

    // Silently restore session from HttpOnly refresh cookie before redirect guard fires.
    restoreSession().then(() => {
      initialized = true;
      const isPublic = isPublicRoute(page.url.pathname);
      if (!get(isAuthenticated) && !isPublic) goto(`/login?redirectTo=${encodeURIComponent(page.url.pathname)}`);

      unsub = auth.subscribe(({ userId, email, isDemo }) => {
        const isPublic = isPublicRoute(page.url.pathname);
        if (!userId) {
          resetUser();
          tierStore.reset();
          resetFeatureFlags();
          resetProfile();
          if (!isPublic) goto(`/login?redirectTo=${encodeURIComponent(page.url.pathname)}`);
        } else if (userId && email) {
          identifyUser(userId, email, isDemo);
          // Warm the billing cache so gated pages render without a waterfall.
          void tierStore.ensure();
          // Load feature flags for this user (tier-resolved).
          void loadFeatureFlags();
        }
      });

      stopPolling = startNotificationPolling();
    });

    return () => {
      unsub?.();
      stopPolling?.();
    };
  });
</script>

<svelte:head>
  <link rel="canonical" href={sharePageUrl} />
  <meta property="og:url" content={sharePageUrl} />
</svelte:head>

<!--
  Render children() in a SINGLE stable location regardless of auth chrome.
  Previously we had two separate {@render children()} calls in the two arms of
  an {#if showMainTabs}…{:else} block: when restoreSession() resolved and
  isAuth flipped true, showMainTabs toggled, Svelte tore down the page from one
  arm and remounted it in the other — firing the page's onMount data fetches a
  SECOND time (the duplicate /dashboard, /payment-rules, /spendable-balances
  calls seen on a fresh load). Toggling only the chrome around a stable child
  keeps the page mounted once.
-->
<DemoBanner />
{#if showMainTabs}
  <DesktopSideNav />
{/if}
<div class={showMainTabs ? 'md:pl-64' : ''}>
  {@render children()}
</div>
{#if showMainTabs}
  <MobileTabBar />
  <PwaInstallBanner />
{/if}
<Toast />
<MfaReauthModal />
